import { useContext, useEffect, useRef, useState } from 'react'
import { Dialog, DialogContent, IconButton, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/CloseOutlined'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline'

import ChangePlanModal from '../../../ui/ChangePlanModal'
import ContractAddonsModal from '../../../ui/ContractAddonsModal'
import CustomButton from '../../../ui/CustomButton'
import { ToastContext } from '../../../../../context/ToastContext'
import { useQueryGetOrganizationTokensAvailable } from '../../../../../queries/organizationQueries'
import { usePlanPermissions } from '../../../../../hooks/usePlanPermissions'

import DefaultQuestions from './DefaultQuestions'
import Messages from './Messages'
import AudioRecorder from './AudioRecorder'
import { useTryLucaChat } from '../context/TryLucaChatContext'

const FilePreview = ({ file, onRemove }) => {
  const [previewUrl, setPreviewUrl] = useState(null)

  useEffect(() => {
    if (!file || !file.type.startsWith('image/')) return

    const url = URL.createObjectURL(file)
    setPreviewUrl(url)

    return () => {
      URL.revokeObjectURL(url)
    }
  }, [file])

  const extension = file.name.split('.').pop()?.toUpperCase() || 'FILE'

  return (
    <div className="inline-flex max-w-full items-center gap-2 rounded-xl border border-accent2/20 bg-white p-1 text-xs text-text shadow-[0_8px_18px_rgba(15,23,42,0.05)] sm:text-sm animate-fade-in">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt={file.name}
          className="h-8 w-8 rounded-lg object-cover border border-accent2/10"
        />
      ) : (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent2/10 text-accent2 font-bold text-[9px] uppercase tracking-wider">
          {extension}
        </div>
      )}
      <div className="flex flex-col min-w-0 pr-1">
        <span className="truncate max-w-[120px] sm:max-w-[180px] font-medium leading-tight text-text">
          {file.name}
        </span>
        <span className="text-[10px] text-muted">
          {(file.size / 1024).toFixed(1)} KB
        </span>
      </div>
      <button
        type="button"
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted hover:bg-danger/10 hover:text-danger transition-colors font-sans font-bold"
        onClick={onRemove}
        aria-label={`Quitar ${file.name}`}
      >
        ✕
      </button>
    </div>
  )
}

const Chat = () => {
  const {
    activeConversation: conversation,
    isLoading,
    limitExceeded,
    messages,
    resetActiveConversation,
    sendMessage,
    setLimitExceeded
  } = useTryLucaChat()
  const [newMessage, setNewMessage] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [files, setFiles] = useState([])
  const [showLimitModal, setShowLimitModal] = useState(false)
  const [showChangePlanModal, setShowChangePlanModal] = useState(false)
  const [showAddonsModal, setShowAddonsModal] = useState(false)
  const [showQuestions, setShowQuestions] = useState(true)
  const [areQuestionsDismissed, setAreQuestionsDismissed] = useState(false)
  const [questionKey, setQuestionKey] = useState(0)
  const { data: tokensInfo } = useQueryGetOrganizationTokensAvailable()
  const messagesContainerRef = useRef(null)
  const questionsRef = useRef(null)
  const fileInputRef = useRef(null)

  const { showToast } = useContext(ToastContext)
  const { canContractAddons } = usePlanPermissions()

  useEffect(() => {
    if (limitExceeded) {
      setShowLimitModal(true)
    }
  }, [limitExceeded])

  useEffect(() => {
    if (messages.length === 0) {
      setShowQuestions(true)
      setQuestionKey(0)
    } else if (!isLoading) {
      setShowQuestions(true)
      setQuestionKey(previousKey => previousKey + 1)
    }
  }, [isLoading, messages.length])

  useEffect(() => {
    if (!conversation.id) {
      return
    }

    setAreQuestionsDismissed(false)
  }, [conversation.id])

  const addNewFilesToState = nextFiles => {
    const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
    const ALLOWED_TYPES = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf', 'text/plain', 'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
      'application/vnd.ms-excel' // xls
    ]

    let hasOversizedFile = false
    let hasInvalidType = false

    setFiles(previousFiles => {
      const existingFileNames = previousFiles.map(file => file.name)
      const mergedFiles = [...previousFiles]

      for (const file of Array.from(nextFiles)) {
        if (file.size > MAX_FILE_SIZE) {
          hasOversizedFile = true
          continue
        }

        const ext = file.name.split('.').pop()?.toLowerCase()
        const isValidExcel = ext === 'xlsx' || ext === 'xls'
        const isValidType = ALLOWED_TYPES.includes(file.type) || isValidExcel

        if (!isValidType) {
          hasInvalidType = true
          continue
        }

        if (!existingFileNames.includes(file.name)) {
          mergedFiles.push(file)
        }
      }

      return mergedFiles
    })

    if (hasOversizedFile) {
      showToast('warning', 'Algunos archivos exceden el límite de tamaño permitido (10MB).')
    }
    if (hasInvalidType) {
      showToast('warning', 'Algunos tipos de archivo no están permitidos (solo imágenes, PDF, Excel, CSV y Texto).')
    }
  }

  const handleSendMessage = async message => {
    if (message.trim() === '') return

    setShowQuestions(false)
    setNewMessage('')
    const selectedFiles = files
    setFiles([])

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    const { success } = await sendMessage(message, selectedFiles)

    if (!success && selectedFiles.length > 0) {
      setFiles(selectedFiles)
    }
  }

  const handleUploadFiles = event => {
    const selectedFiles = event.target.files

    if (!selectedFiles || selectedFiles.length === 0) return

    addNewFilesToState(selectedFiles)

    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }, 0)
  }

  const handleDragOver = event => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }

  const handleDrop = event => {
    event.preventDefault()
    event.stopPropagation()
    const droppedFiles = event.dataTransfer.files

    if (droppedFiles.length > 0) {
      addNewFilesToState(droppedFiles)
    }

    setIsDragging(false)
  }

  const handleDragEnter = event => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = event => {
    event.preventDefault()
    setIsDragging(false)

    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragging(false)
    }
  }

  const handleKeyDown = event => {
    if (isLoading) return

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSendMessage(newMessage)
    }
  }

  const cleanConversation = () => {
    resetActiveConversation()
    setShowQuestions(true)
    setAreQuestionsDismissed(false)
    setQuestionKey(0)
    setNewMessage('')
    setFiles([])

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleTranscriptionComplete = async transcription => {
    if (transcription && transcription.trim()) {
      setNewMessage(transcription)

      try {
        await handleSendMessage(transcription)
      } catch {
        showToast('error', 'Error al enviar el mensaje transcrito')
      }
    }
  }

  const handleTranscriptionError = errorMessage => {
    showToast('error', errorMessage)
  }

  const handlePlanUpdated = () => {
    setShowChangePlanModal(false)
    setLimitExceeded(false)
  }

  const handleAddonSuccess = () => {
    setShowAddonsModal(false)
    setLimitExceeded(false)
  }

  const areQuickSuggestionsVisible = showQuestions && !areQuestionsDismissed
  const isInitialChatState = messages.length === 0 && !isLoading

  return (
    <div
      className="relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-primary/10 bg-gradient-to-br from-white via-surface to-accent2/[0.08] shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:rounded-3xl"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className='border-b border-primary/10 bg-gradient-to-r from-accent2/[0.13] via-white to-primary/[0.05] px-2.5 py-1.5 sm:px-6 sm:py-2'>
        <div className='flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between'>
          <div className='flex min-w-0 flex-col'>
            <div className='flex flex-wrap items-center gap-2'>
              <h1 className="text-[13px] font-semibold text-text sm:text-[20px]">
                Chat principal
              </h1>
            </div>
          </div>
          <div className='flex w-full flex-wrap items-start gap-2 sm:w-auto sm:justify-end'>
            {tokensInfo && (
              <div className="flex w-full min-w-0 flex-wrap items-center gap-1.5 rounded-2xl border border-accent2/20 bg-accent2/[0.07] px-2.5 py-1.5 text-[10px] shadow-[0_10px_24px_rgba(15,23,42,0.05)] sm:w-auto sm:max-w-[420px] sm:text-[13px]">
                {tokensInfo.questionsLeft <= 0 ? (
                  <span className="rounded-full border border-danger/20 bg-danger/10 px-2.5 py-1 text-danger">
                    Sin tokens
                  </span>
                ) : (
                  <>
                    <span className="shrink-0 text-muted">Tokens:</span>
                    <span className="max-w-full break-all rounded-full bg-accent2 px-2.5 py-1 text-[11px] font-semibold text-white shadow-[0_8px_20px_rgba(28,178,175,0.28)] sm:px-3 sm:text-[13px]">
                      {tokensInfo.available.toLocaleString()}
                    </span>
                    <span className="w-full text-muted sm:w-auto">
                      (Aproximadamente {tokensInfo.questionsLeft} preguntas)
                    </span>
                  </>
                )}
              </div>
            )}
            {conversation.id && (
              <Tooltip title="Nuevo chat" placement="top">
                <IconButton
                  aria-label='Nuevo chat'
                  onClick={cleanConversation}
                  size='small'
                  className="h-9 w-9 shrink-0 rounded-xl border border-accent2/20 bg-white text-text shadow-[0_10px_24px_rgba(15,23,42,0.05)] transition-all hover:-translate-y-0.5 hover:border-accent2/50 hover:bg-accent2/[0.08] hover:shadow-[0_14px_28px_rgba(15,23,42,0.08)] sm:h-10 sm:w-10"
                >
                  <DriveFileRenameOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      <div
        className={`flex-1 min-h-0 overflow-x-hidden overflow-y-auto bg-gradient-to-b from-accent2/[0.06] via-white to-primary/[0.03] px-1.5 sm:px-3 ${
          isInitialChatState ? 'py-1.5 sm:py-2.5' : 'py-2 sm:py-3'
        }`}
        ref={messagesContainerRef}
      >
        <div
          className={`flex min-h-full flex-col space-y-2 ${
            isInitialChatState
              ? 'justify-end pb-1.5 sm:pb-2.5'
              : areQuickSuggestionsVisible
              ? 'pb-3 sm:pb-4'
              : ''
          }`}
        >
          <Messages messages={messages} isLoading={isLoading} />
        </div>
      </div>
      <div className="border-t border-primary/10 bg-gradient-to-r from-white via-surface to-accent2/[0.1] px-2 py-1.5 sm:px-4 sm:py-2.5">
        <DefaultQuestions
          handleSendMessage={handleSendMessage}
          onClose={() => setAreQuestionsDismissed(true)}
          showQuestions={areQuickSuggestionsVisible}
          questionKey={questionKey}
          questionsRef={questionsRef}
        />
        <div
          className={`relative overflow-hidden rounded-xl border bg-gradient-to-br from-white via-white to-accent2/[0.06] shadow-[0_14px_28px_rgba(15,23,42,0.06)] transition-colors sm:rounded-2xl ${
            areQuickSuggestionsVisible ? 'mt-1.5 sm:mt-2' : 'mt-0'
          } ${
            isDragging ? 'border-accent2 bg-accent2/10' : 'border-accent2/20'
          }`}
        >
          {files.length > 0 && (
            <div className='flex flex-wrap gap-2 border-b border-accent2/20 bg-accent2/[0.08] px-3 py-2'>
              {files.map((file, index) => (
                <FilePreview
                  key={index}
                  file={file}
                  onRemove={() => setFiles(previousFiles => previousFiles.filter(currentFile => currentFile !== file))}
                />
              ))}
            </div>
          )}
          <div className='relative flex items-center gap-2 px-2.5 py-2 sm:gap-2.5 sm:px-3 sm:py-2.5'>
            <button
              type='button'
              className='inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-accent2/[0.12] hover:text-accent2'
              onClick={() => {
                fileInputRef.current?.click()
              }}
              disabled={limitExceeded}
              title='Adjuntar archivo'
            >
              <AddRoundedIcon sx={{ fontSize: 20 }} />
            </button>
            <input
              type='text'
              className='italic-placeholder h-8 min-w-0 flex-1 border-none bg-transparent text-[14px] text-text placeholder:text-muted focus:outline-none focus:ring-0 sm:text-[14px]'
              value={newMessage}
              onKeyDown={handleKeyDown}
              onChange={event => setNewMessage(event.target.value)}
              placeholder={limitExceeded ? 'Límite alcanzado para este plan' : 'Pregunta lo que quieras'}
              disabled={limitExceeded}
            />
            <div className='h-4 w-px bg-accent2/20 sm:ml-1' />
            <AudioRecorder
              onTranscriptionComplete={handleTranscriptionComplete}
              onError={handleTranscriptionError}
              disabled={limitExceeded || isLoading}
              variant='inlineComposerLight'
            />
            <input
              ref={fileInputRef}
              id="file-upload"
              type="file"
              multiple
              className="hidden"
              onChange={handleUploadFiles}
            />
          </div>
        </div>
      </div>
      <Dialog
        open={showLimitModal}
        maxWidth={false}
        onClose={() => setShowLimitModal(false)}
        sx={{
          '& .MuiDialog-paper': {
            width: {
              xs: 'calc(100% - 1.5rem)',
              sm: 'min(560px, 90vw)',
              md: '40%'
            },
            maxWidth: 'none'
          }
        }}
      >
        <DialogContent className='bg-gray-50 p-6 rounded-lg'>
          <div className='flex justify-between items-center mb-4 border-b pb-2'>
            <h3 className='text-xl font-bold'>Límite de preguntas excedido</h3>
            <button
              className='text-gray-500 hover:text-gray-700'
              onClick={() => setShowLimitModal(false)}
            >
              <CloseIcon />
            </button>
          </div>
          <div className='p-4'>
            <p className='text-base text-gray-700'>
              Has excedido el límite de preguntas permitido. Actualiza tu plan para obtener más tokens.
            </p>
          </div>
          <div className='flex justify-end mt-6 gap-3 flex-wrap'>
            <CustomButton
              title='Cancelar'
              color='primary'
              className='px-4 py-2 rounded-full'
              onClick={() => setShowLimitModal(false)}
            />
            {canContractAddons && (
              <CustomButton
                title='Contratar Add-on'
                color='accent2'
                className='px-4 py-2 rounded-full'
                onClick={() => {
                  setShowLimitModal(false)
                  setShowAddonsModal(true)
                }}
              />
            )}
            <CustomButton
              title='Cambiar Plan'
              color='accent2'
              className='px-4 py-2 rounded-full'
              onClick={() => {
                setShowLimitModal(false)
                setShowChangePlanModal(true)
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      <ChangePlanModal
        isOpen={showChangePlanModal}
        onClose={() => setShowChangePlanModal(false)}
        onPlanUpdated={handlePlanUpdated}
      />
      <ContractAddonsModal
        isOpen={showAddonsModal}
        onClose={() => setShowAddonsModal(false)}
        onSuccess={handleAddonSuccess}
      />
    </div>
  )
}

export default Chat
